import Papa from 'papaparse'
import { pattern, validationContext } from 'validx'

export interface ParsedCustomer {
  email: string
  given_name: string
  family_name: string
  phone_number?: string
}

export interface ParsedCustomerRow extends ParsedCustomer {
  rowNumber: number
  errors: string[]
  isValid: boolean
}

export interface CsvParseResult {
  rows: ParsedCustomerRow[]
  headerError?: string
}

const REQUIRED_HEADERS = ['email', 'first_name', 'last_name']

/**
 * Validate email format.
 */
function isValidEmail(email: string): boolean {
  if (!email || email.length > 320) return false
  const result = validationContext(
    { email },
    { email: [pattern('email')] },
  ).validate()
  return result.isValid
}

/**
 * Validate phone number format.
 */
function isValidPhoneNumber(phone: string): boolean {
  if (!phone) return true
  if (phone.length > 20) return false
  return /^[+]?[ 0-9()-]+$/i.test(phone)
}

/**
 * Normalize header names to expected format.
 */
function normalizeHeader(header: string): string {
  const normalized = header.toLowerCase().trim()
  const mappings: Record<string, string> = {
    email: 'email',
    'email address': 'email',
    email_address: 'email',
    first_name: 'first_name',
    'first name': 'first_name',
    firstname: 'first_name',
    given_name: 'first_name',
    'given name': 'first_name',
    last_name: 'last_name',
    'last name': 'last_name',
    lastname: 'last_name',
    family_name: 'last_name',
    'family name': 'last_name',
    phone_number: 'phone_number',
    'phone number': 'phone_number',
    phone: 'phone_number',
    phonenumber: 'phone_number',
  }
  return mappings[normalized] || normalized
}

/**
 * Parse CSV content into customer data with validation.
 */
export function parseCsvContent(content: string): CsvParseResult {
  const parseResult = Papa.parse<string[]>(content, {
    skipEmptyLines: true,
  })

  if (parseResult.errors.length > 0 && parseResult.data.length === 0) {
    return { rows: [], headerError: 'Failed to parse CSV file' }
  }

  const lines = parseResult.data
  if (lines.length === 0) {
    return { rows: [], headerError: 'CSV file is empty' }
  }

  const rawHeaders = lines[0]
  const headers = rawHeaders.map(normalizeHeader)

  const missingHeaders = REQUIRED_HEADERS.filter((h) => !headers.includes(h))
  if (missingHeaders.length > 0) {
    return {
      rows: [],
      headerError: `Missing required columns: ${missingHeaders.join(', ')}. Expected: email, first_name, last_name, phone_number (optional)`,
    }
  }

  const emailIndex = headers.indexOf('email')
  const firstNameIndex = headers.indexOf('first_name')
  const lastNameIndex = headers.indexOf('last_name')
  const phoneIndex = headers.indexOf('phone_number')

  const rows: ParsedCustomerRow[] = []

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i]

    const email = (values[emailIndex] || '').trim()
    const givenName = (values[firstNameIndex] || '').trim()
    const familyName = (values[lastNameIndex] || '').trim()
    const phoneNumber =
      phoneIndex >= 0 ? (values[phoneIndex] || '').trim() : undefined

    const errors: string[] = []

    if (!email) {
      errors.push('Email is required')
    } else if (!isValidEmail(email)) {
      errors.push('Invalid email format')
    }

    if (!givenName) {
      errors.push('First name is required')
    }

    if (!familyName) {
      errors.push('Last name is required')
    }

    if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
      errors.push('Invalid phone number format')
    }

    rows.push({
      rowNumber: i + 1,
      email: email.toLowerCase(),
      given_name: givenName,
      family_name: familyName,
      phone_number: phoneNumber || undefined,
      errors,
      isValid: errors.length === 0,
    })
  }

  return { rows }
}

/**
 * Read a File object and parse its CSV content.
 */
export async function parseCsvFile(file: File): Promise<CsvParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      const content = e.target?.result
      if (typeof content !== 'string') {
        reject(new Error('Failed to read file content'))
        return
      }
      resolve(parseCsvContent(content))
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsText(file)
  })
}

/**
 * Generate a sample CSV template for download.
 */
export function generateCsvTemplate(): string {
  return `email,first_name,last_name,phone_number
john@example.com,John,Doe,+15551234567
jane@example.com,Jane,Smith,`
}
